// ==========================
// Version 3 — pages/Terms.js (confirmed operator, contacts, and governing law)
// ==========================
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import "./LegalPage.css";

const EFFECTIVE_DATE = "13 August 2026";

export default function Terms() {
  const pageUrl =
    typeof window !== "undefined" ? `${window.location.origin}/terms` : undefined;

  return (
    <div className="legal-page">
      <Seo
        title="Terms of Use • Blessed with a Kiss"
        description="Terms for using Blessed with a Kiss and immortalising a memory."
        image="/logo512.png"
        url={pageUrl}
        type="website"
      />

      <header className="legal-page__hero">
        <div className="legal-page__hero-inner">
          <p className="legal-page__eyebrow">A clear agreement for meaningful memories</p>
          <h1>Terms of Use</h1>
          <p className="legal-page__intro">
            These Terms explain the rules for using Blessed with a Kiss, including the important
            consequences of making a memory public and minting it as an NFT.
          </p>
          <p className="legal-page__date">Effective: {EFFECTIVE_DATE}</p>
        </div>
      </header>

      <div className="legal-page__layout">
        <nav className="legal-page__toc" aria-label="Terms of use contents">
          <strong>On this page</strong>
          <a href="#terms-overview">In short</a>
          <a href="#terms-agreement">Agreement and eligibility</a>
          <a href="#terms-service">The service</a>
          <a href="#terms-content">Your content</a>
          <a href="#terms-minting">Minting and gas</a>
          <a href="#terms-conduct">Acceptable use</a>
          <a href="#terms-moderation">Moderation</a>
          <a href="#terms-closing">Closing an account</a>
          <a href="#terms-liability">Responsibility</a>
          <a href="#terms-contact">Changes and contact</a>
        </nav>

        <article className="legal-page__document">
          <section id="terms-overview" className="legal-page__summary" aria-labelledby="terms-overview-title">
            <h2 id="terms-overview-title">In short</h2>
            <ul>
              <li>You must be at least 18 years old.</li>
              <li>You keep ownership of content you create, subject to the licence needed to operate BWK.</li>
              <li>You must have the right to upload and publish everything you submit.</li>
              <li>BWK does not currently charge a service fee. You pay your own blockchain gas fee when minting.</li>
              <li>Minting is public, involves third-party systems, and cannot be undone.</li>
              <li>We may remove content or restrict accounts to protect people, the service, or the law.</li>
            </ul>
          </section>

          <section id="terms-agreement">
            <h2>1. Agreement and eligibility</h2>
            <p>
              These Terms form an agreement between you and Afrooz Innovations Ltd, company number
              17080521, registered in England and Wales. Afrooz Innovations Ltd operates Blessed
              with a Kiss and is referred to as “BWK”, “we”, or “us”. Please do not use the service
              if you do not agree to these Terms. By continuing to use BWK after having a reasonable
              opportunity to review them, you agree to these Terms and acknowledge our Privacy
              Notice.
            </p>
            <p>
              You must be at least 18 years old and legally able to enter into this agreement. BWK
              is available worldwide, but you may use it only where doing so is lawful. You are
              responsible for complying with laws that apply to you and your content.
            </p>
            <aside className="legal-page__notice">
              General support questions can be sent to{" "}
              <a href="mailto:support@blessedwithakiss.com">
                support@blessedwithakiss.com
              </a>. Privacy questions can be sent to{" "}
              <a href="mailto:privacy@blessedwithakiss.com">
                privacy@blessedwithakiss.com
              </a>.
            </aside>
          </section>

          <section id="terms-service">
            <h2>2. The service and your account</h2>
            <p>
              BWK lets eligible users create memories with stories, photographs, dates, and
              locations, choose supported visibility settings, explore permitted memories on a map,
              link a compatible wallet, and immortalise eligible memories as NFTs.
            </p>
            <p>
              You must provide accurate account information, protect your login details, and keep
              control of your wallet. You are responsible for activity carried out through your
              account or connected wallet unless caused by BWK&apos;s failure to use reasonable care.
              Tell us promptly through the official beta channel if you suspect unauthorised use.
            </p>
            <p>
              We may change, test, suspend, or discontinue features to operate, secure, or improve
              the service. We will try to provide reasonable notice where a significant change
              adversely affects users, unless urgent security, safety, or legal action is needed.
            </p>
          </section>

          <section id="terms-content">
            <h2>3. Your content and permissions</h2>
            <p>
              As between you and BWK, you retain ownership of the original content you submit. You
              grant BWK a worldwide, non-exclusive, royalty-free licence to host, store, reproduce,
              format, display, and transmit that content only as reasonably needed to operate,
              secure, and improve the service according to your visibility choices and these Terms.
            </p>
            <p>
              For public or minted content, this licence includes making it available to other
              users, service providers, IPFS networks, and public blockchain infrastructure. The
              licence continues for copies that cannot reasonably be removed from IPFS, blockchain
              records, backups, or systems controlled by other people.
            </p>
            <p>
              You confirm that you own the content or have all permissions needed to use it this
              way. This includes permission from photographers, copyright owners, and any people
              whose privacy, confidential information, name, image, or story is included. Do not
              publish precise locations or personal details that could put anyone at risk.
            </p>
            <p>
              Minting an NFT does not by itself transfer copyright, image rights, privacy rights,
              or other intellectual-property rights to the NFT holder.
            </p>
          </section>

          <section id="terms-minting">
            <h2>4. Public minting, wallets, and gas fees</h2>
            <aside className="legal-page__warning">
              <strong>Mint only when you are ready to make the memory permanent and public.</strong>
              <p>
                Minting requires the memory, media, story, and saved location to be public. The NFT
                image and metadata may include an image, story excerpt, date, time, location label,
                exact latitude and longitude, internal memory identifier, and public wallet address.
              </p>
            </aside>
            <p>
              When you confirm minting, you instruct BWK and its providers to upload NFT material to
              IPFS and request a transaction through your wallet on the displayed blockchain
              network. Blockchain transactions are normally irreversible. Published information may
              remain available indefinitely and may be copied or indexed by anyone.
            </p>
            <p>
              BWK does not currently charge you a platform or service fee. Your wallet may require
              you to pay a blockchain gas fee to process your mint. The gas fee is a network cost
              paid through your wallet for your transaction. It is not paid to BWK. Gas fees can
              change and may be charged even if a transaction is delayed or fails, depending on the
              network and wallet.
            </p>
            <p>
              You are responsible for checking the network, wallet, transaction details, public
              content, and estimated gas fee before approval. BWK does not control wallets, public
              blockchains, gas prices, IPFS availability, RPC providers, or block explorers. We do
              not promise that an NFT will have financial value, remain accessible through every
              gateway, or be compatible with every marketplace or wallet. BWK is not an investment,
              exchange, custody, or financial-advice service.
            </p>
          </section>

          <section id="terms-conduct">
            <h2>5. Acceptable use</h2>
            <p>You must not use BWK to:</p>
            <ul>
              <li>upload illegal, abusive, threatening, hateful, exploitative, or seriously harmful material;</li>
              <li>share intimate content without consent or content that exploits or endangers a child;</li>
              <li>infringe copyright, privacy, confidentiality, publicity, or other rights;</li>
              <li>impersonate someone, misrepresent ownership, or create deceptive proof;</li>
              <li>publish personal information or precise locations in a way that creates a safety risk;</li>
              <li>interfere with security, probe systems, introduce malicious code, scrape improperly, or bypass access controls;</li>
              <li>use automated systems in a way that overloads or disrupts BWK; or</li>
              <li>use BWK for fraud, money laundering, sanctions evasion, or other unlawful activity.</li>
            </ul>
          </section>

          <section id="terms-moderation">
            <h2>6. Reports, moderation, and complaints</h2>
            <p>
              Users can report memories through available reporting tools. We may investigate,
              restrict visibility, remove BWK-hosted content, preserve evidence, notify authorities,
              or suspend an account where we reasonably believe this is needed for safety, legal
              compliance, rights protection, or enforcement of these Terms.
            </p>
            <p>
              We will aim to consider context and act proportionately. During beta, questions about
              a report or moderation decision can be sent to{" "}
              <a href="mailto:support@blessedwithakiss.com">
                support@blessedwithakiss.com
              </a>. A formal complaints and appeals process is being prepared for general public
              launch.
            </p>
            <p>
              Removing content from the BWK website does not guarantee removal from IPFS, a public
              blockchain, search indexes, third-party services, or copies made by others.
            </p>
          </section>

          <section id="terms-closing">
            <h2>7. Deletion, suspension, and closing an account</h2>
            <p>
              You can edit, change the visibility of, or delete an unminted memory using available
              controls. A minted memory is locked in BWK to preserve consistency with its public NFT
              proof and cannot be edited, made private, or deleted by the owner.
            </p>
            <p>
              Account-level closure tools are still being developed. During beta, you may request
              closure through{" "}
              <a href="mailto:privacy@blessedwithakiss.com">
                privacy@blessedwithakiss.com
              </a>. We may suspend or close an account that materially or repeatedly breaches these
              Terms, creates risk, or must be restricted by law. Where appropriate, we will explain
              the decision and offer a way to raise a complaint.
            </p>
            <p>
              Account closure ends your right to use the service. Provisions concerning public or
              minted content, intellectual property, responsibility, disputes, and records that must
              be retained will continue where their nature requires it.
            </p>
          </section>

          <section>
            <h2>8. Third-party services</h2>
            <p>
              BWK depends on third-party services such as Firebase, Google Maps, reCAPTCHA,
              Thirdweb, WalletConnect, Pinata, wallet providers, IPFS gateways, and public blockchain
              networks. Their own terms and privacy notices may apply. We are not responsible for a
              third-party service outside our reasonable control, but this does not limit any
              responsibility that cannot lawfully be excluded.
            </p>
          </section>

          <section>
            <h2>9. Charges and future paid features</h2>
            <p>
              BWK currently charges no service fee. The only amount a user may pay when minting is
              the blockchain gas fee approved through their own wallet, which is not paid to BWK.
            </p>
            <p>
              If we introduce subscriptions, platform fees, paid features, or other monetisation in
              the future, we will present the price and applicable payment, cancellation, and refund
              terms before you agree to pay. A future paid feature will not be introduced silently
              through these Terms.
            </p>
          </section>

          <section id="terms-liability">
            <h2>10. Availability and responsibility</h2>
            <p>
              BWK is an evolving beta service. We do not promise uninterrupted or error-free access,
              permanent availability through every third-party gateway, or compatibility with every
              device, wallet, or network. Keep your own copies of important content and verify all
              details before minting.
            </p>
            <p>
              Nothing in these Terms excludes or limits responsibility where doing so would be
              unlawful, including responsibility for fraud, fraudulent misrepresentation, or death
              or personal injury caused by negligence. Nothing in these Terms removes consumer
              rights that applicable law does not allow you to waive.
            </p>
            <p>
              Subject to those protections, BWK is not responsible for losses caused solely by your
              disclosure of credentials, wallet decisions, incorrect minting details, unlawful
              content, third-party systems outside our reasonable control, or risks that were clearly
              explained and that you knowingly accepted.
            </p>
          </section>

          <section>
            <h2>11. Intellectual property belonging to BWK</h2>
            <p>
              The BWK name, branding, interface, software, and original service materials belong to
              BWK or its licensors. These Terms do not give you permission to copy, sell, or misuse
              them beyond normal use of the service.
            </p>
          </section>

          <section id="terms-contact">
            <h2>12. Changes, disputes, and contact</h2>
            <p>
              We may update these Terms as the service develops. We will update the effective date
              and provide appropriate notice before a material change takes effect. Continued use
              after that date means you accept the updated Terms where permitted by law. If law
              requires fresh agreement, we will ask for it.
            </p>
            <p>
              Please raise concerns with us first at{" "}
              <a href="mailto:support@blessedwithakiss.com">
                support@blessedwithakiss.com
              </a>{" "}
              so we can try to resolve them. These Terms and disputes or claims arising from them
              are governed by the laws of England and Wales. The courts of England and Wales will
              have jurisdiction, except where mandatory consumer law gives you the right to bring
              proceedings in the courts where you live. Mandatory rights and protections under the
              law where you live continue to apply.
            </p>
          </section>

          <div className="legal-page__actions">
            <Link to="/" className="legal-page__button legal-page__button--secondary">Return home</Link>
            <Link to="/privacy" className="legal-page__button legal-page__button--primary">Read the Privacy Notice</Link>
          </div>
        </article>
      </div>
    </div>
  );
}

// ==========================
// End Version 3 — pages/Terms.js
// ==========================
